/**
 * Subdomain Detection Utility
 * Handles organization and branch context detection across environments.
 *
 * Supports:
 * - Production: branch.client.miralabs.ai → { clientSlug: "client", branchSlug: "branch" }
 * - Development: demo-company.local.test → { clientSlug: "demo-company" }
 * - Localhost/mock mode: query params or local storage overrides
 */

// Mock organizations for development
export const MOCK_ORGANIZATIONS = [
  {
    slug: 'demo-company',
    name: 'MIRA Mortgage LLC',  // Match actual DB organization name
    organization_name: 'MIRA Mortgage LLC',  // Also include alternative field name
    organization_id: 'organization-test-e78ff8ae-7233-4c77-b2b7-b627a031ec7f',  // Actual Stytch ID from DB
    stytch_organization_id: 'organization-test-e78ff8ae-7233-4c77-b2b7-b627a031ec7f',
    company_nmls_id: 'DEMO12345',
    is_active: true
  },
  {
    slug: 'demo-platform',
    name: 'Demo Platform Org',  // The organization you just created
    organization_name: 'Demo Platform Org',
    organization_id: 'organization-test-demo-platform',  // Replace with actual Stytch ID if you have it
    stytch_organization_id: 'organization-test-demo-platform',
    company_nmls_id: 'DEMO99999',
    is_active: true
  },
  {
    slug: 'acmemortgage',
    name: 'Acme Mortgage',
    organization_id: 'organization-test-acme',
    stytch_organization_id: 'organization-test-acme-mortgage',
    company_nmls_id: 'ACM98765',
    is_active: true
  },
  {
    slug: 'mira-platform',
    name: 'MIRA Platform',
    organization_id: 'organization-test-platform',
    stytch_organization_id: 'organization-test-bfa4b3a8-05c1-47dd-9ff3-83bf132e2ee1',
    company_nmls_id: 'MIRA00001',
    is_active: true,
    is_platform: true
  }
];

const SLUG_REGEX = /^[a-z0-9-]{2,63}$/;
const RESERVED_SLUGS = new Set(['www', 'app']);
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);
const DEFAULT_SUFFIXES = ['.demo-platform.miralabs.ai', '.miralabs.ai', '.local.test', '.localhost'];
const CACHE_TTL_MS = 5 * 60 * 1000;
const DETECTED_ORG_KEY = 'detected_organization';
const DETECTED_ORG_TS_KEY = 'detected_organization_timestamp';
const DETECTED_BRANCH_KEY = 'detected_branch';
const DETECTED_BRANCH_TS_KEY = 'detected_branch_timestamp';
const DETECTED_CLIENT_KEY = 'detected_client';
const DETECTED_CLIENT_TS_KEY = 'detected_client_timestamp';
// Known "<branch>-<client>" host labels. Both slugs may contain hyphens, so the
// label alone is ambiguous; entries come from the auth callback, which knows the client slug.
const HOST_SPLITS_KEY = 'mira_tenant_host_splits';

const cacheSlugWithTtl = (key, tsKey, value) => {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return;
  }
  if (!value) {
    sessionStorage.removeItem(key);
    sessionStorage.removeItem(tsKey);
    return;
  }
  sessionStorage.setItem(key, value);
  sessionStorage.setItem(tsKey, Date.now().toString());
};

const getCachedSlugWithTtl = (key, tsKey) => {
  if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
    return null;
  }
  const cachedValue = sessionStorage.getItem(key);
  const cachedTimestamp = sessionStorage.getItem(tsKey);
  if (!cachedValue || !cachedTimestamp) {
    return null;
  }
  const age = Date.now() - parseInt(cachedTimestamp, 10);
  if (Number.isNaN(age) || age > CACHE_TTL_MS) {
    sessionStorage.removeItem(key);
    sessionStorage.removeItem(tsKey);
    return null;
  }
  return cachedValue;
};

const normalizeSlug = (slug) => {
  if (!slug) return null;
  const candidate = slug.toLowerCase();
  if (RESERVED_SLUGS.has(candidate)) return null;
  if (!SLUG_REGEX.test(candidate)) return null;
  return candidate;
};

const readHostSplits = () => {
  try {
    if (typeof localStorage === 'undefined') return {};
    const parsed = JSON.parse(localStorage.getItem(HOST_SPLITS_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Record how a combined "<branch>-<client>" host label splits, so parseHostname
 * doesn't have to guess for hyphenated branch slugs (e.g. "austin-branch-loaninghub").
 */
export const rememberHostSplit = ({ clientSlug, branchSlug } = {}) => {
  const client = normalizeSlug(clientSlug);
  const branch = normalizeSlug(branchSlug);
  if (!client || !branch) return;
  try {
    if (typeof localStorage === 'undefined') return;
    const splits = readHostSplits();
    splits[`${branch}-${client}`] = { clientSlug: client, branchSlug: branch };
    localStorage.setItem(HOST_SPLITS_KEY, JSON.stringify(splits));
  } catch {
    // Storage unavailable; parseHostname falls back to the first-hyphen split.
  }
};

const buildAllowedSuffixes = (baseDomain) => {
  const suffixes = new Set(DEFAULT_SUFFIXES);
  if (baseDomain) {
    const normalized = baseDomain.startsWith('.') ? baseDomain.toLowerCase() : `.${baseDomain.toLowerCase()}`;
    suffixes.add(normalized);
  }
  return Array.from(suffixes).sort((a, b) => b.length - a.length);
};

/**
 * Return the raw tenant label from a hostname, before any branch/client split.
 *
 * For "austin-branch-loaninghub.local.test" this is "austin-branch-loaninghub".
 * Callers need the unsplit label because the branch/client split below is
 * ambiguous: the backend joins branch and org with a single hyphen
 * (TENANT_BRANCH_SEPARATOR), and either slug may itself contain hyphens.
 */
export const getTenantHostLabel = (hostname) => {
  const host = (hostname ?? (typeof window !== 'undefined' ? window.location.hostname : ''))
    .toLowerCase()
    .split(':')[0];
  if (!host || LOCAL_HOSTS.has(host)) return null;
  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) return null;

  const allowedSuffixes = buildAllowedSuffixes(import.meta.env?.VITE_APP_BASE_DOMAIN);
  const matchedSuffix = allowedSuffixes.find((suffix) => host.endsWith(suffix));
  if (!matchedSuffix) return null;

  const labels = host.slice(0, -matchedSuffix.length).split('.').filter(Boolean);
  return labels.length ? labels[labels.length - 1] || null : null;
};

/**
 * Does the current host belong to the given organization slug?
 *
 * The backend composes the tenant label as `{branchSlug}-{orgSlug}` (see
 * _build_tenant_slug in app/api/v1/routes/auth/discovery.py), so the org slug
 * is always either the whole label or its trailing segment. Matching on that
 * suffix is unambiguous even when the branch slug contains hyphens
 * ("austin-branch") or when there is no branch at all ("demo-platform") —
 * both of which the first-hyphen split in parseHostname gets wrong.
 */
export const hostBelongsToOrgSlug = (orgSlug, hostname) => {
  const normalizedOrg = normalizeSlug(orgSlug);
  if (!normalizedOrg) return false;

  const label = getTenantHostLabel(hostname);
  if (!label) return false;

  return label === normalizedOrg || label.endsWith(`-${normalizedOrg}`);
};

/**
 * Parse a hostname and extract client/branch slugs.
 *
 * @param {string} hostname - Hostname to parse
 * @param {Object} options - Optional overrides
 * @param {string[]} [options.allowedSuffixes] - Explicit suffix list
 * @param {string} [options.baseDomain] - Preferred base domain (e.g. miralabs.ai)
 * @returns {{ type: string, clientSlug: string|null, branchSlug: string|null, hostname: string|null }}
 */
export const parseHostname = (hostname, options = {}) => {
  const host = (hostname || '').toLowerCase().split(':')[0];
  const result = {
    type: 'unknown',
    clientSlug: null,
    branchSlug: null,
    parentSlug: null,
    hostname: host || null,
  };

  if (!host) {
    return result;
  }

  if (LOCAL_HOSTS.has(host)) {
    return { ...result, type: 'localhost' };
  }

  if (/^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return { ...result, type: 'ip' };
  }

  const allowedSuffixes = options.allowedSuffixes || buildAllowedSuffixes(options.baseDomain || import.meta.env?.VITE_APP_BASE_DOMAIN);
  const matchedSuffix = allowedSuffixes.find((suffix) => host.endsWith(suffix));
  if (!matchedSuffix) {
    return result;
  }

  const labelPart = host.slice(0, -matchedSuffix.length);
  const labels = labelPart.split('.').filter(Boolean);
  if (!labels.length) {
    return result;
  }

  let clientCandidate = null;
  let branchCandidate = null;
  let parentCandidate = null;

  if (labels.length === 1) {
    // Backend joins branch + org with a hyphen as a single subdomain label
    // (e.g. "branch-testt.local.test"). Use a recorded split when we have one,
    // otherwise split on the first hyphen to extract both.
    const combined = labels[0];
    const knownSplit = readHostSplits()[combined];
    const hyphenIdx = combined.indexOf('-');
    if (knownSplit?.clientSlug && knownSplit?.branchSlug) {
      branchCandidate = normalizeSlug(knownSplit.branchSlug);
      clientCandidate = normalizeSlug(knownSplit.clientSlug);
    } else if (hyphenIdx > 0 && hyphenIdx < combined.length - 1) {
      branchCandidate = normalizeSlug(combined.substring(0, hyphenIdx));
      clientCandidate = normalizeSlug(combined.substring(hyphenIdx + 1));
    } else {
      clientCandidate = normalizeSlug(combined);
    }
  } else if (labels.length === 2) {
    branchCandidate = normalizeSlug(labels[0]);
    clientCandidate = normalizeSlug(labels[1]);
  } else {
    branchCandidate = normalizeSlug(labels[labels.length - 3]);
    clientCandidate = normalizeSlug(labels[labels.length - 2]);
    parentCandidate = normalizeSlug(labels[labels.length - 1]);
  }

  if (!clientCandidate) {
    return result;
  }

  let type = 'client';
  if (branchCandidate) {
    type = 'branch';
  }
  if (clientCandidate === 'platform' && !branchCandidate) {
    type = 'platform';
  }

  return {
    type,
    clientSlug: clientCandidate,
    branchSlug: branchCandidate,
    parentSlug: parentCandidate,
    hostname: host,
  };
};

/**
 * Record a host's split when the URL names it, before anything else parses the
 * host, so session storage scopes, redirects and the org lookup use the real
 * branch/client:
 *
 * - The magic-link shim lands on "<branch>-<client>.<domain>/auth/callback?slug=<client>"
 *   (plus "branch=<branch>" for B2C).
 * - The login page sends an employee to their home branch's host as
 *   "<branch>-<client>.<domain>/login/b2b?slug=<client>&branch=<branch>", from
 *   what resolve-login returned. A browser that has never finished a login on
 *   that host has no recorded split, and the first-hyphen guess is wrong for a
 *   hyphenated branch ("austin-branch").
 */
const primeHostSplitFromUrl = () => {
  if (typeof window === 'undefined' || !window.location) return;
  const { hostname, pathname, search } = window.location;
  const isCallback = /\/auth\/callback\/?$/.test(pathname || '');
  const isLogin = /\/login(\/|$)/.test(pathname || '');
  if (!isCallback && !isLogin) return;

  const params = new URLSearchParams(search);
  const client = normalizeSlug(params.get('slug'));
  if (!client) return;

  const label = (hostname || '').toLowerCase().split('.')[0];
  const suffix = `-${client}`;
  const branchParam = normalizeSlug(params.get('branch') || params.get('branch_slug'));
  if (branchParam) {
    if (label === `${branchParam}${suffix}`) {
      rememberHostSplit({ clientSlug: client, branchSlug: branchParam });
    }
  } else if (isCallback && label.length > suffix.length && label.endsWith(suffix)) {
    rememberHostSplit({ clientSlug: client, branchSlug: label.slice(0, -suffix.length) });
  }
};

primeHostSplitFromUrl();

/**
 * Every way a combined "<branch>-<client>" host label could split, client slug
 * shortest first: the order the backend resolves them in
 * (`resolve_org_and_branch_from_slug`). Empty when the host is not a single
 * combined label, or when its split is already recorded.
 *
 * For use when the client slug parseHostname guessed is not an organization:
 * try each candidate until one is, then `rememberHostSplit` it.
 */
export const hostSplitCandidates = (hostname) => {
  const label = getTenantHostLabel(hostname);
  if (!label || !label.includes('-')) return [];
  if (readHostSplits()[label]) return [];

  const candidates = [];
  for (let idx = label.lastIndexOf('-'); idx > 0; idx = label.lastIndexOf('-', idx - 1)) {
    const branchSlug = normalizeSlug(label.slice(0, idx));
    const clientSlug = normalizeSlug(label.slice(idx + 1));
    if (branchSlug && clientSlug) candidates.push({ clientSlug, branchSlug });
  }
  return candidates;
};

/**
 * Backwards-compatible helper that returns only the client slug.
 * @deprecated Prefer parseHostname for branch-aware parsing.
 */
export const extractSubdomainFromHost = (hostname) => {
  const { clientSlug } = parseHostname(hostname);
  return clientSlug;
};

/**
 * Get subdomain from current context
 * @returns {string|null} - The detected subdomain or null
 */
export const getSubdomain = () => {
  const hostname = window.location.hostname;
  const searchParams = new URLSearchParams(window.location.search);
  
  console.log('[Subdomain] Detection started for hostname:', hostname);
  
  // Priority 1: Check query parameters (for development overrides)
  // Support both 'org' and 'queryorg' parameters
  const orgParam = searchParams.get('org') || searchParams.get('queryorg');
  const branchParam = searchParams.get('branch') || searchParams.get('branchSlug') || searchParams.get('branch_slug');
  if (orgParam) {
    console.log('[Subdomain] Detected from query param:', orgParam);
    cacheSlugWithTtl(DETECTED_ORG_KEY, DETECTED_ORG_TS_KEY, orgParam);
    if (branchParam) {
      cacheSlugWithTtl(DETECTED_BRANCH_KEY, DETECTED_BRANCH_TS_KEY, branchParam);
    }
    return orgParam;
  }
  if (branchParam) {
    console.log('[Subdomain] Detected branch from query param:', branchParam);
    cacheSlugWithTtl(DETECTED_BRANCH_KEY, DETECTED_BRANCH_TS_KEY, branchParam);
  }
  
  // Priority 2: Extract from hostname (development and production)
  const { clientSlug, branchSlug } = parseHostname(hostname);
  if (clientSlug || branchSlug) {
    const effectiveSlug = branchSlug || clientSlug;
    console.log('[Subdomain] Detected from hostname:', effectiveSlug, branchSlug ? `branch=${branchSlug}` : '');
    cacheSlugWithTtl(DETECTED_ORG_KEY, DETECTED_ORG_TS_KEY, effectiveSlug);
    if (branchSlug) {
      cacheSlugWithTtl(DETECTED_BRANCH_KEY, DETECTED_BRANCH_TS_KEY, branchSlug);
    } else {
      cacheSlugWithTtl(DETECTED_BRANCH_KEY, DETECTED_BRANCH_TS_KEY, null);
    }
    cacheSlugWithTtl(DETECTED_CLIENT_KEY, DETECTED_CLIENT_TS_KEY, clientSlug);
    return clientSlug;
  }
  
  // Priority 2.5: Check session cache (valid for 5 minutes)
  const cachedOrg = getCachedSlugWithTtl(DETECTED_ORG_KEY, DETECTED_ORG_TS_KEY);
  if (cachedOrg) {
    console.log('[Subdomain] Using cached organization:', cachedOrg);
    return cachedOrg;
  }
  
  // Priority 3: Check localStorage mock (fallback for testing)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const mockOrg = localStorage.getItem('mock_organization');
    if (mockOrg) {
      console.log('[Subdomain] Using mock organization from localStorage:', mockOrg);
      return mockOrg;
    }
  }
  
  console.log('[Subdomain] No subdomain detected for hostname:', hostname);
  return null;
};

/**
 * Set mock organization for development
 * @param {string} subdomain - The subdomain to mock
 */
export const setMockOrganization = (subdomain) => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    localStorage.setItem('mock_organization', subdomain);
    console.log('[Subdomain] Mock organization set:', subdomain);
  } else {
    console.warn('[Subdomain] Mock organization can only be set in development mode');
  }
};

/**
 * Clear mock organization
 */
export const clearMockOrganization = () => {
  localStorage.removeItem('mock_organization');
  console.log('[Subdomain] Mock organization cleared');
};

/**
 * Clear cached organization detection
 */
export const clearCachedOrganization = () => {
  cacheSlugWithTtl(DETECTED_ORG_KEY, DETECTED_ORG_TS_KEY, null);
  cacheSlugWithTtl(DETECTED_BRANCH_KEY, DETECTED_BRANCH_TS_KEY, null);
  cacheSlugWithTtl(DETECTED_CLIENT_KEY, DETECTED_CLIENT_TS_KEY, null);
  console.log('[Subdomain] Cached organization cleared');
};

/**
 * Persist a validated tenant context so subsequent helpers stop relying on stale hostnames.
 * Accepts partial data so callers can update whichever identifiers they have.
 */
export const rememberTenantContext = ({ orgSlug, clientSlug, branchSlug } = {}) => {
  const normalizeForCache = (value) => (value === null ? null : normalizeSlug(value));

  if (typeof orgSlug !== 'undefined') {
    cacheSlugWithTtl(DETECTED_ORG_KEY, DETECTED_ORG_TS_KEY, normalizeForCache(orgSlug));
  }

  if (typeof clientSlug !== 'undefined') {
    cacheSlugWithTtl(DETECTED_CLIENT_KEY, DETECTED_CLIENT_TS_KEY, normalizeForCache(clientSlug));
  }

  if (typeof branchSlug !== 'undefined') {
    cacheSlugWithTtl(DETECTED_BRANCH_KEY, DETECTED_BRANCH_TS_KEY, normalizeForCache(branchSlug));
  }
};

/**
 * Get mock organization for development
 * @param {string} slug - The organization slug
 * @returns {Object|null} - The mock organization or null
 */
export const getMockOrganization = (slug) => {
  return MOCK_ORGANIZATIONS.find(org => org.slug === slug) || null;
};

const getWindowHostname = () => (typeof window !== 'undefined' ? window.location.hostname : '');

export const getCurrentClientSlug = () => {
  const { clientSlug } = parseHostname(getWindowHostname());
  if (clientSlug) {
    cacheSlugWithTtl(DETECTED_CLIENT_KEY, DETECTED_CLIENT_TS_KEY, clientSlug);
    return clientSlug;
  }
  return getCachedSlugWithTtl(DETECTED_CLIENT_KEY, DETECTED_CLIENT_TS_KEY);
};

export const getCurrentParentSlug = () => {
  const { parentSlug } = parseHostname(getWindowHostname());
  return parentSlug;
};

export const getCurrentBranchSlug = () => {
  const { branchSlug } = parseHostname(getWindowHostname());
  if (branchSlug) {
    cacheSlugWithTtl(DETECTED_BRANCH_KEY, DETECTED_BRANCH_TS_KEY, branchSlug);
    return branchSlug;
  }

  if (typeof window !== 'undefined') {
    const searchParams = new URLSearchParams(window.location.search);
    const branchParam = searchParams.get('branch') || searchParams.get('branchSlug') || searchParams.get('branch_slug');
    if (branchParam) {
      cacheSlugWithTtl(DETECTED_BRANCH_KEY, DETECTED_BRANCH_TS_KEY, branchParam);
      return branchParam;
    }

    const cachedBranch = getCachedSlugWithTtl(DETECTED_BRANCH_KEY, DETECTED_BRANCH_TS_KEY);
    if (cachedBranch) {
      return cachedBranch;
    }
  }

  return null;
};

/**
 * Check if running in development mode
 * @returns {boolean} - True if in development mode
 */
export const isDevelopmentMode = () => {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.endsWith('.local.test')
  );
};

/**
 * Build organization URL based on environment
 * @param {string} subdomain - The organization subdomain
 * @param {string} path - The path to append
 * @returns {string} - The complete URL
 */
export const buildOrganizationUrl = (subdomain, path = '') => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // Development mode
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const baseUrl = `${protocol}//${hostname}${port ? `:${port}` : ''}`;
    return `${baseUrl}${path}?org=${subdomain}`;
  }

  // local.test development
  if (hostname.endsWith('.local.test')) {
    return `${protocol}//${subdomain}.local.test${port ? `:${port}` : ''}${path}`;
  }

  // Production mode — use base domain from env
  const baseDomain = import.meta.env.VITE_APP_BASE_DOMAIN || 'miralabs.ai';
  return `${protocol}//${subdomain}.${baseDomain}${path}`;
};
